import type { TemplateScoringCategory } from '@piccolohealth/pbs-common';
import { ChakraV3, DndKitCore, DndKitSortable, DragHandle, Sortable } from '@piccolohealth/ui';
import { uuid } from '@piccolohealth/util';
import React from 'react';
import { useController, useFieldArray } from 'react-hook-form';
import { FaPlus } from 'react-icons/fa';
import type { FormValues } from './TemplateScoringChecklistEditor';
import { TemplateScoringCriterias } from './TemplateScoringCriterias';

interface TemplateScoringCategoryItemProps {
  category: TemplateScoringCategory;
  index: number;
  onRemove: () => void;
}

const TemplateScoringCategoryItem = (props: TemplateScoringCategoryItemProps) => {
  const { category, index, onRemove } = props;

  const {
    field: { value, onChange },
  } = useController({
    name: `categories.${index}.description`,
  });

  const [inputValue, setInputValue] = React.useState(value);
  const ref = React.useRef(null);

  const { attributes, setNodeRef, listeners, transform, transition } = DndKitSortable.useSortable({
    id: category.id,
  });

  const onEdit = React.useCallback(
    (value: string) => {
      onChange(value);
    },
    [onChange],
  );

  return (
    <Sortable ref={setNodeRef} transform={transform} transition={transition}>
      <ChakraV3.Accordion.Root
        collapsible
        borderWidth='1px'
        borderColor='gray.200'
        borderRadius='xl'
        bg='white'
        overflow='hidden'
        defaultValue={['system-prompt']}
      >
        <ChakraV3.Accordion.Item border={0} value='system-prompt'>
          <ChakraV3.Editable.Root
            value={inputValue}
            onValueChange={({ value }) => setInputValue(value)}
            onValueCommit={({ value }) => onEdit(value)}
            activationMode='none'
            className='group'
          >
            <ChakraV3.Accordion.ItemTrigger
              ref={ref}
              p={0}
              _hover={{ bg: 'gray.50' }}
              bg='gray.50'
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <ChakraV3.HStack w='full' gap={1} px={2} py={4} onClick={(e) => e.stopPropagation()}>
                <DragHandle listeners={listeners} attributes={attributes} size='sm' />
                <ChakraV3.Text fontSize='lg' fontWeight='semibold' flexShrink={0}>
                  {index + 1} -
                </ChakraV3.Text>
                <ChakraV3.Editable.Preview
                  w='full'
                  cursor='pointer'
                  fontSize='lg'
                  fontWeight='semibold'
                  textAlign='left'
                  p={0}
                  rounded='none'
                />
                <ChakraV3.Editable.Input
                  fontSize='lg'
                  fontWeight='semibold'
                  textAlign='left'
                  p={0}
                  rounded='none'
                  h='auto'
                />
                <ChakraV3.Spacer />
                <ChakraV3.EditableControls onRemove={onRemove} />
              </ChakraV3.HStack>
              <ChakraV3.Stack py={2} pr={4}>
                <ChakraV3.Accordion.ItemIndicator />
              </ChakraV3.Stack>
            </ChakraV3.Accordion.ItemTrigger>
          </ChakraV3.Editable.Root>
          <ChakraV3.Accordion.ItemContent pb={4} pt={2} px={2}>
            <TemplateScoringCriterias categoryIndex={index} />
          </ChakraV3.Accordion.ItemContent>
        </ChakraV3.Accordion.Item>
      </ChakraV3.Accordion.Root>
    </Sortable>
  );
};

export const TemplateScoringCategories = () => {
  const { fields, append, remove, move } = useFieldArray<FormValues, 'categories'>({
    name: 'categories',
  });

  const categories = fields as unknown as TemplateScoringCategory[];

  const sensors = DndKitCore.useSensors(
    DndKitCore.useSensor(DndKitCore.PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const onDragEnd = React.useCallback(
    async (event: DndKitCore.DragEndEvent) => {
      const { active, over } = event;

      if (!active || !over) {
        return;
      }

      const oldIndex = categories.findIndex((item) => item.id === active.id);
      const newIndex = categories.findIndex((item) => item.id === over.id);

      if (oldIndex === newIndex) {
        return;
      }

      move(oldIndex, newIndex);
    },
    [categories, move],
  );

  const onAdd = React.useCallback(() => {
    append({
      id: uuid(),
      description: 'Example category',
      criterias: [],
    });
  }, [append]);

  return (
    <ChakraV3.Stack gap={4}>
      <DndKitCore.DndContext
        sensors={sensors}
        collisionDetection={DndKitCore.closestCenter}
        onDragEnd={onDragEnd}
      >
        <DndKitSortable.SortableContext
          strategy={DndKitSortable.verticalListSortingStrategy}
          items={categories.map(({ id }) => id)}
        >
          {categories.map((category, index) => (
            <TemplateScoringCategoryItem
              key={category.id}
              index={index}
              category={category}
              onRemove={() => remove(index)}
            />
          ))}
        </DndKitSortable.SortableContext>
      </DndKitCore.DndContext>

      <ChakraV3.Button
        onClick={onAdd}
        size='sm'
        variant='outline'
        borderWidth='2px'
        borderStyle='dashed'
        color='secondary'
      >
        <FaPlus />
        Add category
      </ChakraV3.Button>
    </ChakraV3.Stack>
  );
};
